import { faFloppyDisk, faPen, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import LoadingIndicator from '@/components/LoadingIndicator';
import { userAtom } from '@/store/user.store';
import type { UserInfo } from '@/types/common';

import {
  mapUserNotificationSettingsToFormFields,
  useFetchNotificationSettings,
  useUpdateNotificationPreferences
} from './NotificationSettings.store';
import useStyles from './NotificationSettings.styles';
import type { NotificationSettingsFormFields } from './NotificationSettings.types';
import NotificationSettingsForm from './NotificationSettingsForm';

const NotificationSettings = () => {
  const [userData] = useAtom(userAtom);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { classes } = useStyles();
  const { isFetching, user } = useFetchNotificationSettings(
    userData as UserInfo
  );

  const form = useForm({
    values: mapUserNotificationSettingsToFormFields(user)
  });

  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    reset
  } = form;

  //-----------------------------------------------------------------
  const errorMessage = useMemo(() => {
    if (errors.notification_ticket_event) {
      return (
        errors.notification_ticket_event.message ||
        'Please select at least one ticket event.'
      );
    }
    return null;
  }, [errors.notification_ticket_event]);

  const { mutate: updateNotificationPreferences } =
    useUpdateNotificationPreferences();

  const onSubmit = (data: NotificationSettingsFormFields) => {
    clearErrors();

    const requestBody = {
      ...data,
      notification_threat_priority: [
        data.notification_ticket_threat_priority_p1 ? 1 : null,
        data.notification_ticket_threat_priority_p2 ? 2 : null,
        data.notification_ticket_threat_priority_p3 ? 3 : null,
        data.notification_ticket_threat_priority_p4 ? 4 : null,
        data.notification_ticket_threat_priority_p5 ? 5 : null
      ].filter((priority) => priority !== null) as number[],
      notification_ticket_event: [
        data.notification_ticket_event_create && 'CREATE',
        data.notification_ticket_event_update && 'UPDATE'
      ].filter(Boolean) as ('CREATE' | 'UPDATE')[]
    };

    updateNotificationPreferences(requestBody, {
      onSuccess: () => {
        // make the saved values the baseline so Cancel restores them, not the pre-save values
        reset(data);
        setIsEditing(false);
      }
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Card>
          <CardHeader
            className={classes.cardHeader}
            title="Notifications"
            slotProps={{ title: { variant: 'h4' } }}
            subheader={
              isEditing ? (
                <Stack direction="row">
                  <IconButton type="submit">
                    <FontAwesomeIcon size="xs" icon={faFloppyDisk} />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      reset();
                      setIsEditing(!isEditing);
                    }}
                  >
                    <FontAwesomeIcon size="xs" icon={faXmark} />
                  </IconButton>
                </Stack>
              ) : (
                <IconButton onClick={() => setIsEditing(!isEditing)}>
                  <FontAwesomeIcon size="xs" icon={faPen} />
                </IconButton>
              )
            }
          />
          <CardContent>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {errorMessage}
              </Alert>
            )}
            <Typography variant="h5">
              <b>Please choose when to receive emails.</b>
            </Typography>
            {isFetching && <LoadingIndicator isLoading />}
            {!isFetching && <NotificationSettingsForm isEditing={isEditing} />}
            {isEditing && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Changes to your notification preferences will apply to all
                organizations you have access to in Scope.
              </Alert>
            )}
          </CardContent>
        </Card>
      </form>
    </FormProvider>
  );
};

export default NotificationSettings;
